import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';
import { copyText } from '../../lib/clipboard';
import { useAuth } from '../auth/AuthProvider';
import { getDeviceCredentials } from './api';
import { generateDevicePassword } from './password';
import { toProblem } from './problem';

/** How long a revealed password stays on screen. */
const REVEAL_MS = 60_000;

/**
 * The password typed or generated for a device. A new device starts with a
 * generated password, shown so the installer can configure it on the device.
 */
export function DevicePasswordInput({
  label,
  value,
  error,
  generated,
  onChange,
}: {
  label: string;
  value: string;
  error: string | undefined;
  /** The value was generated by the platform (a new device). */
  generated: boolean;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(generated);
  return (
    <Field
      label={label}
      error={error}
      hint={
        generated
          ? 'Senha gerada pelo Pro Guard: configure esta mesma senha no aparelho. Se ele já tiver uma senha, digite-a no lugar. Fica guardada criptografada.'
          : 'Armazenada criptografada.'
      }
    >
      {({ id, describedBy, invalid }) => (
        <div className="flex items-center gap-1">
          <input
            id={id}
            type={visible ? 'text' : 'password'}
            autoComplete="new-password"
            spellCheck={false}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className={`${inputClass} font-mono`}
          />
          <Button
            variant="ghost"
            className="h-9 shrink-0 px-2"
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={visible}
            onClick={() => {
              setVisible((v) => !v);
            }}
          >
            {visible ? (
              <EyeOff aria-hidden="true" className="size-4" />
            ) : (
              <Eye aria-hidden="true" className="size-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            className="h-9 shrink-0 px-2"
            aria-label="Gerar senha aleatória"
            title="Gerar senha aleatória"
            onClick={() => {
              onChange(generateDevicePassword());
              setVisible(true);
            }}
          >
            <RefreshCw aria-hidden="true" className="size-4" />
          </Button>
        </div>
      )}
    </Field>
  );
}

/**
 * Shows a device's stored password to holders of devices.password. Every view
 * is audited on the server; the password leaves the screen after a minute.
 */
export function SavedPassword({ deviceId }: { deviceId: string }) {
  const { request } = useAuth();
  const [password, setPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const reveal = useMutation({
    mutationFn: () => getDeviceCredentials(request, deviceId),
    onSuccess: (c) => {
      setPassword(c.password);
    },
  });

  useEffect(() => {
    if (password === null) return;
    const t = window.setTimeout(() => {
      setPassword(null);
    }, REVEAL_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, [password]);

  if (password === null) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          className="h-7"
          disabled={reveal.isPending}
          onClick={() => {
            reveal.mutate();
          }}
        >
          <Eye aria-hidden="true" className="size-3.5" />
          {reveal.isPending ? 'Buscando…' : 'Mostrar senha'}
        </Button>
        <span className="text-2xs text-fg-subtle">
          Quem vê a senha fica registrado na auditoria.
        </span>
        {reveal.isError && (
          <p role="alert" className="w-full text-xs text-crit">
            {toProblem(reveal.error).form ?? 'Não foi possível mostrar a senha.'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5" aria-live="polite">
      {password ? (
        <code className="min-w-0 flex-1 rounded border border-line-strong bg-surface px-2 py-1 font-mono text-xs break-all text-fg">
          {password}
        </code>
      ) : (
        <span className="flex-1 text-xs text-fg-muted">Nenhuma senha guardada.</span>
      )}
      {password && (
        <Button
          variant="ghost"
          className="h-7 shrink-0 px-2"
          aria-label="Copiar senha"
          onClick={() => {
            void copyText(password).then((ok) => {
              if (!ok) return;
              setCopied(true);
              window.setTimeout(() => {
                setCopied(false);
              }, 1500);
            });
          }}
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3.5" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )}
        </Button>
      )}
      <Button
        variant="ghost"
        className="h-7 shrink-0"
        onClick={() => {
          setPassword(null);
        }}
      >
        <EyeOff aria-hidden="true" className="size-3.5" />
        Ocultar
      </Button>
    </div>
  );
}
